import { useState, useEffect } from "react";
import { Cog, Plus, RefreshCw, CheckCircle, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import axios from "axios";

export default function ERPProduction() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("bom");
  const [materials, setMaterials] = useState([]);
  const [boms, setBoms] = useState([]);
  const [workCenters, setWorkCenters] = useState([]);
  const [productionOrders, setProductionOrders] = useState([]);
  const [showNewBOM, setShowNewBOM] = useState(false);
  const [showNewWC, setShowNewWC] = useState(false);
  const [showNewPO, setShowNewPO] = useState(false);

  const [newBOM, setNewBOM] = useState({ material_id: "", components: [{ material_id: "", quantity: 1 }] });
  const [newWC, setNewWC] = useState({ work_center_code: "", description: "", capacity_per_day: 8, efficiency_rate: 100 });
  const [newProdOrder, setNewProdOrder] = useState({ material_id: "", planned_quantity: 1, bom_id: "", work_center_id: "" });
  const [selectedBomMaterial, setSelectedBomMaterial] = useState("");

  const fetchMaterials = async () => {
    try { const res = await axios.get("/api/erp/materials"); setMaterials(res.data.materials || []); } catch (err) { /* ignore */ }
  };

  const fetchBOMs = async (materialId) => {
    if (!materialId) return;
    try { const res = await axios.get("/api/erp/bom/" + materialId); setBoms(res.data.boms || []); } catch (err) { toast({ title: "Failed to load BOMs", variant: "destructive" }); }
  };

  const fetchWorkCenters = async () => {
    try { const res = await axios.get("/api/erp/work-centers"); setWorkCenters(res.data.work_centers || []); } catch (err) { /* ignore */ }
  };

  const fetchProductionOrders = async () => {
    try { const res = await axios.get("/api/erp/production-orders"); setProductionOrders(res.data.production_orders || []); } catch (err) { /* ignore */ }
  };

  useEffect(() => { fetchMaterials(); fetchWorkCenters(); fetchProductionOrders(); }, []);
  useEffect(() => { if (selectedBomMaterial) fetchBOMs(selectedBomMaterial); }, [selectedBomMaterial]);

  const handleCreateBOM = async () => {
    try { await axios.post("/api/erp/bom", newBOM); toast({ title: "BOM created" }); setShowNewBOM(false); if (newBOM.material_id) fetchBOMs(newBOM.material_id); } catch (err) { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleCreateWC = async () => {
    try { await axios.post("/api/erp/work-centers", newWC); toast({ title: "Work center created" }); setShowNewWC(false); fetchWorkCenters(); } catch (err) { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleCreateProdOrder = async () => {
    try { await axios.post("/api/erp/production-orders", newProdOrder); toast({ title: "Production order created" }); setShowNewPO(false); fetchProductionOrders(); } catch (err) { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleComplete = async (orderId, completedQty) => {
    try { await axios.post("/api/erp/production-orders/" + orderId + "/complete", { completed_quantity: completedQty, rejected_quantity: 0 }); toast({ title: "Production order completed" }); fetchProductionOrders(); } catch (err) { toast({ title: "Failed", variant: "destructive" }); }
  };

  const renderBOM = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Bills of Material</h2>
        <Dialog open={showNewBOM} onOpenChange={setShowNewBOM}>
          <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> New BOM</Button></DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>New Bill of Material</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Finished Product</Label>
                <Select value={newBOM.material_id} onValueChange={v => setNewBOM(p => ({ ...p, material_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
                  <SelectContent>{materials.filter(m => m.material_type === "FINISHED").map(m => <SelectItem key={m.id} value={m.id}>{m.material_code} - {m.description}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Components</span>
                <Button variant="outline" size="sm" onClick={() => setNewBOM(p => ({ ...p, components: [...p.components, { material_id: "", quantity: 1 }] }))}>Add</Button>
              </div>
              {newBOM.components.map((comp, i) => (
                <div key={i} className="flex gap-2">
                  <div className="flex-1">
                    <Select value={comp.material_id} onValueChange={v => { const comps = [...newBOM.components]; comps[i].material_id = v; setNewBOM(p => ({ ...p, components: comps })); }}>
                      <SelectTrigger><SelectValue placeholder="Component" /></SelectTrigger>
                      <SelectContent>{materials.map(m => <SelectItem key={m.id} value={m.id}>{m.material_code} - {m.description}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="w-20"><Input type="number" placeholder="Qty" value={comp.quantity} onChange={e => { const comps = [...newBOM.components]; comps[i].quantity = parseFloat(e.target.value) || 0; setNewBOM(p => ({ ...p, components: comps })); }} /></div>
                </div>
              ))}
              <Button onClick={handleCreateBOM} className="w-full">Create BOM</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="mb-4">
        <Label>Select Product</Label>
        <Select value={selectedBomMaterial} onValueChange={setSelectedBomMaterial}>
          <SelectTrigger><SelectValue placeholder="Choose a product to view BOM" /></SelectTrigger>
          <SelectContent>{materials.filter(m => m.material_type === "FINISHED").map(m => <SelectItem key={m.id} value={m.id}>{m.material_code} - {m.description}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {selectedBomMaterial && boms.length > 0 ? boms.map(bom => (
        <Card key={bom.id} className="mb-3">
          <CardHeader><CardTitle className="text-sm">BOM: {bom.bom_code}</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow><TableHead>Component</TableHead><TableHead>Qty</TableHead><TableHead>UOM</TableHead></TableRow></TableHeader>
              <TableBody>{bom.components.map((comp, i) => {
                const mat = materials.find(m => m.id === comp.material_id);
                return <TableRow key={i}><TableCell>{mat?.description || comp.material_id}</TableCell><TableCell>{comp.quantity}</TableCell><TableCell>{comp.unit_of_measure || "EA"}</TableCell></TableRow>;
              })}</TableBody>
            </Table>
          </CardContent>
        </Card>
      )) : selectedBomMaterial ? <p className="text-muted-foreground">No BOM defined for this product</p> : <p className="text-muted-foreground">Select a product to view its BOM</p>}
    </div>
  );

  const renderWorkCenters = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Work Centers</h2>
        <Dialog open={showNewWC} onOpenChange={setShowNewWC}>
          <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Work Center</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Work Center</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Code</Label><Input value={newWC.work_center_code} onChange={e => setNewWC(p => ({ ...p, work_center_code: e.target.value }))} /></div>
              <div><Label>Description</Label><Input value={newWC.description} onChange={e => setNewWC(p => ({ ...p, description: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Capacity/Day</Label><Input type="number" value={newWC.capacity_per_day} onChange={e => setNewWC(p => ({ ...p, capacity_per_day: parseFloat(e.target.value) || 0 }))} /></div>
                <div><Label>Efficiency %</Label><Input type="number" value={newWC.efficiency_rate} onChange={e => setNewWC(p => ({ ...p, efficiency_rate: parseFloat(e.target.value) || 0 }))} /></div>
              </div>
              <Button onClick={handleCreateWC} className="w-full">Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Code</TableHead><TableHead>Description</TableHead><TableHead>Capacity/Day</TableHead><TableHead>Efficiency</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {workCenters.map(wc => <TableRow key={wc.id}><TableCell className="font-mono">{wc.work_center_code}</TableCell><TableCell>{wc.description}</TableCell><TableCell>{wc.capacity_per_day}h</TableCell><TableCell>{wc.efficiency_rate}%</TableCell></TableRow>)}
            {workCenters.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No work centers</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const renderProductionOrders = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Production Orders</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchProductionOrders}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          <Dialog open={showNewPO} onOpenChange={setShowNewPO}>
            <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Order</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Production Order</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Product</Label>
                  <Select value={newProdOrder.material_id} onValueChange={v => setNewProdOrder(p => ({ ...p, material_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
                    <SelectContent>{materials.map(m => <SelectItem key={m.id} value={m.id}>{m.material_code} - {m.description}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Planned Quantity</Label><Input type="number" value={newProdOrder.planned_quantity} onChange={e => setNewProdOrder(p => ({ ...p, planned_quantity: parseInt(e.target.value) || 0 }))} /></div>
                <div><Label>Work Center</Label>
                  <Select value={newProdOrder.work_center_id} onValueChange={v => setNewProdOrder(p => ({ ...p, work_center_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select work center" /></SelectTrigger>
                    <SelectContent>{workCenters.map(wc => <SelectItem key={wc.id} value={wc.id}>{wc.work_center_code}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button onClick={handleCreateProdOrder} className="w-full">Create</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Order #</TableHead><TableHead>Product</TableHead><TableHead>Planned</TableHead><TableHead>Completed</TableHead><TableHead>Status</TableHead><TableHead>Action</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {productionOrders.map(po => {
              const mat = materials.find(m => m.id === po.material_id);
              return (
                <TableRow key={po.id}>
                  <TableCell className="font-mono text-xs">{po.order_number}</TableCell>
                  <TableCell>{mat?.description || po.material_id}</TableCell>
                  <TableCell>{po.planned_quantity}</TableCell>
                  <TableCell>{po.completed_quantity || 0}</TableCell>
                  <TableCell>
                    <Badge variant={po.status === "COMPLETED" ? "default" : po.status === "IN_PROCESS" ? "warning" : "secondary"}>{po.status}</Badge>
                    {po.material_shortage && <AlertTriangle className="h-3 w-3 inline ml-1 text-red-500" />}
                  </TableCell>
                  <TableCell>{po.status !== "COMPLETED" && <Button size="sm" variant="outline" onClick={() => handleComplete(po.id, po.planned_quantity)}><CheckCircle className="h-3 w-3 mr-1" /> Complete</Button>}</TableCell>
                </TableRow>
              );
            })}
            {productionOrders.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No production orders</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl font-bold">Production Planning</h1><p className="text-muted-foreground">BOM, work centers & production orders</p></div>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="bom">Bills of Material</TabsTrigger>
          <TabsTrigger value="work-centers">Work Centers</TabsTrigger>
          <TabsTrigger value="production-orders">Production Orders</TabsTrigger>
        </TabsList>
        <TabsContent value="bom" className="mt-4">{renderBOM()}</TabsContent>
        <TabsContent value="work-centers" className="mt-4">{renderWorkCenters()}</TabsContent>
        <TabsContent value="production-orders" className="mt-4">{renderProductionOrders()}</TabsContent>
      </Tabs>
    </div>
  );
}
